/**
 * **Utenti per azienda** — la vista multi-azienda delle associazioni utente↔azienda.
 *
 * È la pagina chiesta il 27 ago 2026 («da un tenant master dovrei poter vedere/gestire gli utenti
 * di tutti i tenant») e **non costruita** il giorno dopo, perché `GET /api/user-tenants` non aveva
 * nessuno scoping server-side: costruirla allora avrebbe voluto dire fondare una schermata di
 * amministrazione su un buco di lettura (`docs/FE-TO-BE-USER-TENANTS-CROSS-TENANT-v1.md`).
 *
 * Il BE ha chiuso il buco il 28 ago (`BE-RESPONSE-TO-FE-USER-TENANTS-CROSS-TENANT-v1.md`): la
 * risorsa ha un perimetro, calcolato da `ITenantVisibilityResolver` — il tenant corrente sempre,
 * più il gruppo con `canSeeAllTenants`, più i discendenti per un ruolo globale. Da qui la pagina
 * non chiede «tutte le aziende»: chiede il **proprio perimetro**, e il server decide qual è.
 *
 * ── Le tre decisioni che si vedono a schermo ──
 *
 * · **L'albero viene da `/api/tenants/getTree`, non da `GET /api/tenants`.** Sono due insiemi
 *   diversi per scelta del BE: l'elenco aziende risponde «dove posso andare» e comprende le
 *   aziende in cui ho solo una riga di appartenenza; l'albero risponde «cosa amministro», ed è
 *   **lo stesso** insieme che il server usa come perimetro di `user-tenants`. Con l'elenco
 *   avremmo disegnato nodi che rispondono zero righe — e fuori perimetro la risposta è `200` con
 *   zero righe, non `403`, quindi non avremmo nemmeno potuto dire perché.
 * · **«Tutto il perimetro» è una richiesta legittima, non una scorciatoia.** Senza filtro di
 *   azienda il server risponde tutto il perimetro amministrabile: è la vista che serve per
 *   cercare una persona quando non si sa in quale azienda stia. La colonna «Azienda» compare solo
 *   in quella modalità, dove è l'unica cosa che distingue due righe uguali.
 * · **Il conteggio per nodo è una richiesta per azienda** (`page.size:1` → `meta.total`), e si
 *   spegne oltre le 50 aziende: su un'installazione grande sarebbe una tempesta di chiamate per
 *   un numero accessorio. Quando è spento non si mostra uno zero — si mostra niente, che è la
 *   verità.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Badge, Button, Card, Group, Select, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconArrowRight, IconBuildingCommunity, IconChevronRight, IconUsers } from '@tabler/icons-react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { MaeAsync } from '@/shared/components/MaeAsync';
import { StatCard } from '@/shared/components/StatCard';
import { mw, MW_CARD_SHADOW } from '@/shared/mobile/tokens';
import { useAuth } from '@/app/providers/AuthProvider';
import { appiattisciAlbero, contaAssociazioni, useAlberoAmministrabile } from '@/features/tenants/api';
import { UserTenantsManager } from '@/features/tenants/UserTenantsManager';

export const Route = createFileRoute('/_app/admin/tenant-users')({ component: TenantUsersPage });

/** Oltre questa soglia i conteggi per azienda diventano più chiamate che informazione. */
const MAX_COUNTS = 50;

/** La voce «tutto il perimetro» nella select mobile. Non stringa vuota: per Mantine quella vale
 *  «nessuna scelta», e l'opzione non si sarebbe potuta selezionare. */
const ALL = '__perimetro__';

function TenantUsersPage() {
  const isMobile = useIsMobileShell();
  const { decoded } = useAuth();
  const currentTenant = decoded?.tenantId ?? '';
  const isSuper = decoded?.superadmin === 'True';

  /** `null` = tutto il perimetro; altrimenti l'azienda scelta. */
  const [selection, setSelection] = useState<string | null>(null);

  /* Stessa porta dell'organigramma e della tendina azienda: una chiave, una cache. Qui c'era
     `['tenant-tree-administrable']`, cioè la stessa `GET /api/tenants/getTree` una seconda
     volta. */
  const treeQ = useAlberoAmministrabile();
  const rows = useMemo(() => appiattisciAlbero(treeQ.data ?? []), [treeQ.data]);
  const activeCounts = rows.length > 0 && rows.length <= MAX_COUNTS;

  const countsQ = useQueries({
    queries: rows.map((r) => ({
      queryKey: ['tenant-users-count', r.id],
      queryFn: () => contaAssociazioni(r.id),
      enabled: activeCounts,
      staleTime: 60_000,
    })),
  });
  const counts = useMemo(() => {
    const m = new Map<string, number | undefined>();
    rows.forEach((r, i) => m.set(r.id, countsQ[i]?.data));
    return m;
  }, [rows, countsQ]);

  const scopeTotalQ = useQuery({
    queryKey: ['tenant-users-count', 'perimetro'],
    queryFn: () => contaAssociazioni(null),
    staleTime: 60_000,
  });

  const selectedName = rows.find((r) => r.id === selection)?.nome;
  const singleCompany = rows.length === 1;

  const kpi = (
    <Group grow={!isMobile} wrap="wrap" gap="md" align="stretch">
      <StatCard
        label="Aziende che amministri" icon={IconBuildingCommunity} color="brand"
        value={rows.length} loading={treeQ.isLoading} error={treeQ.isError}
      />
      <StatCard
        label="Associazioni nel perimetro" icon={IconUsers} color="green"
        value={scopeTotalQ.data ?? 0} loading={scopeTotalQ.isLoading} error={scopeTotalQ.isError}
      />
    </Group>
  );

  /** L'elenco rientrato delle aziende. Su mobile diventa una select: un albero in 360px non si usa. */
  const selector = isMobile ? (
    <Select
      label="Azienda"
      description="Oppure tutto il perimetro"
      data={[
        { value: ALL, label: `Tutto il perimetro (${rows.length} aziende)` },
        ...rows.map((r) => ({ value: r.id, label: `${'· '.repeat(r.livello)}${r.nome}` })),
      ]}
      value={selection ?? ALL}
      onChange={(v) => setSelection(!v || v === ALL ? null : v)}
      allowDeselect={false}
      searchable={rows.length > 8}
    />
  ) : (
    <Card withBorder radius="lg" padding={6} style={{ boxShadow: MW_CARD_SHADOW, borderColor: mw.line, width: 300, flexShrink: 0, alignSelf: 'flex-start' }}>
      <Stack gap={2}>
        <TreeEntry
          label="Tutto il perimetro"
          detail={`${rows.length} ${rows.length === 1 ? 'azienda' : 'aziende'}`}
          count={scopeTotalQ.data}
          level={0}
          active={selection === null}
          onClick={() => setSelection(null)}
        />
        {rows.map((r) => (
          <TreeEntry
            key={r.id}
            label={r.nome}
            detail={r.id.toLowerCase() === currentTenant.toLowerCase() ? 'azienda corrente' : undefined}
            count={activeCounts ? counts.get(r.id) : undefined}
            level={r.livello}
            active={selection === r.id}
            onClick={() => setSelection(r.id)}
          />
        ))}
      </Stack>
    </Card>
  );

  const content = (
    <MaeAsync
      query={[treeQ]}
      loading="Caricamento delle aziende che amministri…"
      empty={rows.length === 0 && {
        title: 'Nessuna azienda nel tuo perimetro',
        description: 'Il sistema non riporta nessuna azienda amministrabile da questa sessione: succede se l’azienda del token non esiste più o se la sessione è stata aperta su un’azienda di sistema. Rientra scegliendo l’azienda dal selettore in alto.',
        icon: IconBuildingCommunity,
      }}
    >
      <Stack gap="md">
        {singleCompany && (
          <MaeAdvice
            tone="info"
            title="Il tuo perimetro contiene una sola azienda"
            icon={IconBuildingCommunity}
          >
            Da questa sessione amministri solo <b>{rows[0]?.nome}</b>, quindi questa pagina mostra
            quello che mostra già la tab «Utenti dell’azienda» di Permessi, ruoli &amp; rotte. Il perimetro si
            allarga con il claim <code>canSeeAllTenants</code> (il gruppo societario) o{' '}
            <code>canManageSubTenants</code> (le sotto-aziende){isSuper ? '' : ', che questa utenza non ha'}.
          </MaeAdvice>
        )}
        {!activeCounts && rows.length > MAX_COUNTS && (
          <MaeAdvice tone="info" title="Conteggi per azienda non mostrati">
            Il perimetro conta {rows.length} aziende: un numero accanto a ciascuna vorrebbe dire
            {' '}{rows.length} chiamate a ogni apertura della pagina. Il totale del perimetro sta nella
            scheda in alto; il numero di una singola azienda lo vedi selezionandola.
          </MaeAdvice>
        )}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          {isMobile ? null : selector}
          <div style={{ flex: 1, minWidth: 0 }}>
            <UserTenantsManager
              tenantId={selection}
              tenantNome={selection ? selectedName : undefined}
              mostraAzienda={selection === null}
            />
          </div>
        </div>
      </Stack>
    </MaeAsync>
  );

  if (isMobile) {
    return (
      <Stack gap={16} px={20} pt={16} pb={24}>
        <MobileHeader title="Utenti per azienda" />
        {kpi}
        {selector}
        {content}
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconUsers size={22} />}
        title="Utenti per azienda"
        sub="Le associazioni utente↔azienda di tutte le aziende che amministri, in una schermata. Il perimetro lo decide il sistema."
        actions={
          <Button component={Link} to="/admin/tenant-management" variant="light" rightSection={<IconArrowRight size={16} />}>
            Permessi, ruoli &amp; rotte
          </Button>
        }
      />
      <Stack gap="lg">
        {kpi}
        {content}
      </Stack>
    </MaeShell>
  );
}

/** Una riga dell'albero: rientro per livello, conteggio a destra quando c'è. */
function TreeEntry({
  label, detail, count, level, active, onClick,
}: {
  label: string;
  detail?: string;
  count?: number;
  level: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      className="mw-pressable"
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '8px 10px', paddingLeft: 10 + level * 16, borderRadius: 9,
        background: active ? mw.accentTint : 'transparent',
        color: active ? mw.accentText : mw.ink900,
        fontFamily: 'inherit', textAlign: 'left',
      }}
      aria-current={active ? 'true' : undefined}
    >
      {level > 0 && <IconChevronRight size={13} style={{ flexShrink: 0, opacity: 0.5 }} />}
      <span style={{ minWidth: 0, flex: 1 }}>
        <Text size="sm" fw={active ? 700 : 500} truncate>{label}</Text>
        {detail && <Text size="xs" c="dimmed">{detail}</Text>}
      </span>
      {count != null && (
        <Badge size="sm" variant={active ? 'filled' : 'light'} color={active ? 'azzurro' : 'gray'}>
          {count}
        </Badge>
      )}
    </UnstyledButton>
  );
}
